/**
 * The Talas bitmap font.
 *
 * Written by hand rather than pulled off a font site for one reason: Serbian Latin needs
 * Č Ć Ž Š Đ č ć ž š đ, and nearly every good pixel font stops at ASCII. A student reading
 * "Zavrsni detalji" instead of "Završni detalji" is a small thing that makes a product feel like
 * it was made for somebody else.
 *
 * The cell is 5 wide by 11 tall:
 *
 *   rows 0-1    diacritic zone (only uppercase reaches up here)
 *   rows 2-8    main band, 7 rows — caps fill it, lowercase x-height is rows 4-8
 *   rows 9-10   descenders
 *
 * Accented letters are composed, not drawn twice: Č is C plus a caron. That is what keeps the
 * accents identical across the alphabet, which is exactly what hand-drawing them would not.
 *
 * The same table is the source for two things: the canvas renderer in the game world, and a real
 * TTF generated at build time (scripts/build-font.mjs) so the DOM uses the identical letterforms.
 */

export const CELL_W = 5;
export const CELL_H = 11;
export const BAND_TOP = 2; // first row of the 7-row main band
export const BASELINE = 8; // last row of the main band
export const LINE_HEIGHT = 13;

type Rows = readonly string[];

/** Main band: 7 rows of 5 columns. `#` is ink. */
const CAPS: Record<string, Rows> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.####'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['.###.', '..#..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  J: ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  M: ['#...#', '##.##', '#.#.#', '#.#.#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '##..#', '#.#.#', '#..##', '#..##', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],
  // D with stroke — cannot be composed, the bar crosses the stem.
  Đ: ['.####', '.#..#', '###.#', '.#..#', '.#..#', '.#..#', '.####'],
};

const LOWER: Record<string, Rows> = {
  a: ['.....', '.....', '.###.', '....#', '.####', '#...#', '.####'],
  b: ['#....', '#....', '####.', '#...#', '#...#', '#...#', '####.'],
  c: ['.....', '.....', '.###.', '#....', '#....', '#....', '.###.'],
  d: ['....#', '....#', '.####', '#...#', '#...#', '#...#', '.####'],
  e: ['.....', '.....', '.###.', '#...#', '#####', '#....', '.###.'],
  f: ['..##.', '.#...', '####.', '.#...', '.#...', '.#...', '.#...'],
  g: ['.....', '.....', '.####', '#...#', '#...#', '#...#', '.####'],
  h: ['#....', '#....', '####.', '#...#', '#...#', '#...#', '#...#'],
  i: ['..#..', '.....', '.##..', '..#..', '..#..', '..#..', '.###.'],
  j: ['...#.', '.....', '..##.', '...#.', '...#.', '...#.', '...#.'],
  k: ['#....', '#....', '#..#.', '#.#..', '##...', '#.#..', '#..#.'],
  l: ['.##..', '..#..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  m: ['.....', '.....', '##.#.', '#.#.#', '#.#.#', '#...#', '#...#'],
  n: ['.....', '.....', '####.', '#...#', '#...#', '#...#', '#...#'],
  o: ['.....', '.....', '.###.', '#...#', '#...#', '#...#', '.###.'],
  p: ['.....', '.....', '####.', '#...#', '#...#', '#...#', '####.'],
  q: ['.....', '.....', '.####', '#...#', '#...#', '#...#', '.####'],
  r: ['.....', '.....', '#.##.', '##..#', '#....', '#....', '#....'],
  s: ['.....', '.....', '.####', '#....', '.###.', '....#', '####.'],
  t: ['.#...', '.#...', '####.', '.#...', '.#...', '.#..#', '..##.'],
  u: ['.....', '.....', '#...#', '#...#', '#...#', '#...#', '.####'],
  v: ['.....', '.....', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  w: ['.....', '.....', '#...#', '#...#', '#.#.#', '#.#.#', '.#.#.'],
  x: ['.....', '.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#'],
  y: ['.....', '.....', '#...#', '#...#', '#...#', '#...#', '.####'],
  z: ['.....', '.....', '#####', '...#.', '..#..', '.#...', '#####'],
  đ: ['....#', '..###', '.####', '#...#', '#...#', '#...#', '.####'],
};

const DIGITS: Record<string, Rows> = {
  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['#####', '...#.', '..#..', '...#.', '....#', '#...#', '.###.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],
};

const PUNCT: Record<string, Rows> = {
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.##..', '.##..'],
  ',': ['.....', '.....', '.....', '.....', '.....', '.##..', '.##..'],
  ':': ['.....', '.##..', '.##..', '.....', '.##..', '.##..', '.....'],
  ';': ['.....', '.##..', '.##..', '.....', '.##..', '.##..', '.....'],
  '!': ['..#..', '..#..', '..#..', '..#..', '..#..', '.....', '..#..'],
  '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
  "'": ['..#..', '..#..', '.....', '.....', '.....', '.....', '.....'],
  '"': ['.#.#.', '.#.#.', '.....', '.....', '.....', '.....', '.....'],
  '„': ['.....', '.....', '.....', '.....', '.....', '.#.#.', '#.#..'],
  '”': ['.#.#.', '#.#..', '.....', '.....', '.....', '.....', '.....'],
  '(': ['...#.', '..#..', '.#...', '.#...', '.#...', '..#..', '...#.'],
  ')': ['.#...', '..#..', '...#.', '...#.', '...#.', '..#..', '.#...'],
  '[': ['..##.', '..#..', '..#..', '..#..', '..#..', '..#..', '..##.'],
  ']': ['.##..', '..#..', '..#..', '..#..', '..#..', '..#..', '.##..'],
  '-': ['.....', '.....', '.....', '.###.', '.....', '.....', '.....'],
  '–': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
  '—': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
  _: ['.....', '.....', '.....', '.....', '.....', '.....', '#####'],
  '/': ['....#', '....#', '...#.', '..#..', '.#...', '#....', '#....'],
  '\\': ['#....', '#....', '.#...', '..#..', '...#.', '....#', '....#'],
  '|': ['..#..', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....'],
  '=': ['.....', '.....', '#####', '.....', '#####', '.....', '.....'],
  '<': ['...#.', '..#..', '.#...', '#....', '.#...', '..#..', '...#.'],
  '>': ['.#...', '..#..', '...#.', '....#', '...#.', '..#..', '.#...'],
  '%': ['##..#', '##.#.', '..#..', '.#...', '#.##.', '...##', '#....'],
  '&': ['.##..', '#..#.', '#.#..', '.#...', '#.#.#', '#..#.', '.##.#'],
  '@': ['.###.', '#...#', '#.###', '#.#.#', '#.###', '#....', '.###.'],
  '#': ['.#.#.', '#####', '.#.#.', '.#.#.', '#####', '.#.#.', '.....'],
  '*': ['.....', '#.#.#', '.###.', '#####', '.###.', '#.#.#', '.....'],
  '~': ['.....', '.....', '.##.#', '#..#.', '.....', '.....', '.....'],
  '·': ['.....', '.....', '.....', '..#..', '.....', '.....', '.....'],
  '…': ['.....', '.....', '.....', '.....', '.....', '.....', '#.#.#'],
  '°': ['.##..', '#..#.', '.##..', '.....', '.....', '.....', '.....'],
  '€': ['..###', '.#...', '####.', '.#...', '####.', '.#...', '..###'],
  $: ['..#..', '.####', '#.#..', '.###.', '..#.#', '####.', '..#..'],
  '→': ['.....', '.....', '..#..', '...#.', '#####', '...#.', '..#..'],
  '←': ['.....', '.....', '..#..', '.#...', '#####', '.#...', '..#..'],
  '↑': ['.....', '..#..', '.###.', '#.#.#', '..#..', '..#..', '..#..'],
  '↓': ['.....', '..#..', '..#..', '..#..', '#.#.#', '.###.', '..#..'],
  '▸': ['.....', '.#...', '.##..', '.###.', '.##..', '.#...', '.....'],
  '▾': ['.....', '.....', '#####', '.###.', '.###.', '..#..', '.....'],
  '✓': ['.....', '....#', '...#.', '#..#.', '.##..', '..#..', '.....'],
  '✕': ['.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '.....'],
  '★': ['..#..', '..#..', '#####', '.###.', '.#.#.', '#...#', '.....'],
  '♦': ['..#..', '.###.', '#####', '.###.', '..#..', '.....', '.....'],
  '●': ['.....', '.###.', '#####', '#####', '#####', '.###.', '.....'],
  '◆': ['..#..', '.###.', '#####', '.###.', '..#..', '.....', '.....'],
  '⏱': ['..#..', '.###.', '#.#.#', '#.##.', '#...#', '.###.', '.....'],
  '💬': ['#####', '#...#', '#...#', '#####', '.#...', '#....', '.....'],
  '🔒': ['.###.', '#...#', '#####', '#.#.#', '#####', '.....', '.....'],
};

/** Two rows drawn above a letter. */
const MARKS: Record<string, Rows> = {
  caron: ['.#.#.', '..#..'],
  acute: ['...#.', '..#..'],
};

const COMPOSED: Record<string, { base: string; mark: keyof typeof MARKS }> = {
  Č: { base: 'C', mark: 'caron' },
  Ć: { base: 'C', mark: 'acute' },
  Š: { base: 'S', mark: 'caron' },
  Ž: { base: 'Z', mark: 'caron' },
  č: { base: 'c', mark: 'caron' },
  ć: { base: 'c', mark: 'acute' },
  š: { base: 's', mark: 'caron' },
  ž: { base: 'z', mark: 'caron' },
};

/** Rows 9-10 of the cell, for the six letters that drop below the baseline. */
const DESCENDERS: Record<string, Rows> = {
  g: ['....#', '.###.'],
  j: ['#..#.', '.##..'],
  p: ['#....', '#....'],
  q: ['....#', '....#'],
  y: ['....#', '.###.'],
  ',': ['..#..', '.#...'],
  ';': ['..#..', '.#...'],
  '„': ['..#..', '#....'],
};

export interface Glyph {
  /** CELL_H rows of CELL_W columns, `#` for ink. */
  rows: string[];
  /** Ink extent, used for proportional spacing. -1 when the glyph is blank. */
  left: number;
  right: number;
  advance: number;
}

const EMPTY_ROW = '.'.repeat(CELL_W);

function buildGlyph(char: string): Glyph {
  const rows = Array.from({ length: CELL_H }, () => EMPTY_ROW);

  const composed = COMPOSED[char];
  const baseChar = composed ? composed.base : char;
  const band = CAPS[baseChar] ?? LOWER[baseChar] ?? DIGITS[baseChar] ?? PUNCT[baseChar];
  if (!band) return { rows, left: -1, right: -1, advance: 3 };

  for (let i = 0; i < 7; i++) rows[BAND_TOP + i] = band[i] ?? EMPTY_ROW;

  if (composed) {
    const mark = MARKS[composed.mark]!;
    // Uppercase reaches the top of the cell, so its accent sits above it. Lowercase has two empty
    // rows of its own at the top of the band, so the accent drops into those and stays tight.
    const offset = baseChar === baseChar.toUpperCase() && CAPS[baseChar] ? 0 : BAND_TOP;
    rows[offset] = mark[0]!;
    rows[offset + 1] = mark[1]!;
  }

  const tail = DESCENDERS[baseChar];
  if (tail) {
    rows[9] = tail[0]!;
    rows[10] = tail[1]!;
  }

  let left = CELL_W;
  let right = -1;
  for (const row of rows) {
    for (let x = 0; x < CELL_W; x++) {
      if (row[x] === '#') {
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }

  if (right < 0) return { rows, left: -1, right: -1, advance: 3 };
  return { rows, left, right, advance: right - left + 2 };
}

const glyphCache = new Map<string, Glyph>();

export function glyph(char: string): Glyph {
  let found = glyphCache.get(char);
  if (!found) {
    found = buildGlyph(char);
    glyphCache.set(char, found);
  }
  return found;
}

export function hasGlyph(char: string): boolean {
  if (char === ' ') return true;
  const composed = COMPOSED[char];
  const base = composed ? composed.base : char;
  return Boolean(CAPS[base] ?? LOWER[base] ?? DIGITS[base] ?? PUNCT[base]);
}

/** Everything the font can draw — the build script iterates this to emit a real TTF. */
export function charset(): string[] {
  return [
    ...Object.keys(CAPS),
    ...Object.keys(LOWER),
    ...Object.keys(DIGITS),
    ...Object.keys(PUNCT),
    ...Object.keys(COMPOSED),
  ];
}

/* ------------------------------------------------------------------ measuring & drawing */

export interface TextStyle {
  /** Pixels per font pixel. */
  scale?: number;
  /** Fixed-width cells, for numbers that must not jitter as they count up. */
  mono?: boolean;
  /** Extra pixels between glyphs, in font pixels. */
  tracking?: number;
}

export function advanceOf(char: string, style: TextStyle = {}): number {
  if (char === ' ') return (style.mono ? CELL_W + 1 : 3) + (style.tracking ?? 0);
  if (style.mono) return CELL_W + 1 + (style.tracking ?? 0);
  return glyph(char).advance + (style.tracking ?? 0);
}

export function measureText(text: string, style: TextStyle = {}): number {
  const scale = style.scale ?? 1;
  let width = 0;
  for (const char of text) width += advanceOf(char, style);
  return Math.max(0, width - (style.tracking ?? 0)) * scale;
}

/**
 * Word wrap that measures in real glyph widths rather than character counts — with proportional
 * spacing, counting characters puts the break in the wrong place often enough to notice.
 */
export function wrapText(text: string, maxWidth: number, style: TextStyle = {}): string[] {
  const scale = style.scale ?? 1;
  const limit = maxWidth / scale;
  const lines: string[] = [];

  /**
   * A word longer than the whole line has to be cut, not pushed out.
   *
   * Nothing in the curriculum is one long unbroken string, but a dialogue line is typed by a person
   * into a box, and a pasted URL or a runaway keyboard would otherwise render as a single row wider
   * than the buffer — text spilling across the map with no way to read the rest of it.
   */
  const breakWord = (word: string): string[] => {
    const pieces: string[] = [];
    let piece = '';
    for (const char of word) {
      const next = piece + char;
      if (piece && measureText(next, { ...style, scale: 1 }) > limit) {
        pieces.push(piece);
        piece = char;
      } else {
        piece = next;
      }
    }
    if (piece) pieces.push(piece);
    return pieces;
  };

  for (const paragraph of text.split('\n')) {
    let line = '';
    let width = 0;
    for (const word of paragraph.split(' ')) {
      for (const part of measureText(word, { ...style, scale: 1 }) > limit ? breakWord(word) : [word]) {
        const partWidth = measureText(part, { ...style, scale: 1 });
        const spaceWidth = line ? advanceOf(' ', style) : 0;
        if (line && width + spaceWidth + partWidth > limit) {
          lines.push(line);
          line = part;
          width = partWidth;
        } else {
          line = line ? `${line} ${part}` : part;
          width += spaceWidth + partWidth;
        }
      }
    }
    lines.push(line);
  }
  return lines;
}

/**
 * Draws with fillRect per pixel run rather than per pixel — a line of text is a few dozen rects
 * instead of a few hundred, which matters when the dialogue box types itself out every frame.
 */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  style: TextStyle & { color?: string; shadow?: string } = {},
): number {
  const scale = style.scale ?? 1;
  let cursor = x;

  const paint = (color: string, offsetX: number, offsetY: number) => {
    ctx.fillStyle = color;
    let pen = cursor;
    for (const char of text) {
      if (char !== ' ') {
        const g = glyph(char);
        const originX = style.mono ? 0 : g.left < 0 ? 0 : g.left - 1;
        for (let row = 0; row < CELL_H; row++) {
          const line = g.rows[row]!;
          let run = -1;
          for (let col = 0; col <= CELL_W; col++) {
            const ink = col < CELL_W && line[col] === '#';
            if (ink && run < 0) run = col;
            if (!ink && run >= 0) {
              ctx.fillRect(
                pen + (run - originX) * scale + offsetX,
                y + row * scale + offsetY,
                (col - run) * scale,
                scale,
              );
              run = -1;
            }
          }
        }
      }
      pen += advanceOf(char, style) * scale;
    }
  };

  if (style.shadow) paint(style.shadow, scale, scale);
  paint(style.color ?? '#ffffff', 0, 0);

  for (const char of text) cursor += advanceOf(char, style) * scale;
  return cursor - x;
}
